import { supabase } from "@/integrations/supabase/client";
import { setUser as setSentryUser } from "@/services/sentry";
import { identify as posthogIdentify, reset as posthogReset } from "@/services/posthog";
import { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  userId: string | null;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  loading: true,
  userId: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string|null>(null);
  const [loading, setLoading] = useState(true);

  const handleAuthStateChange = useCallback(
    (event: AuthChangeEvent, session: Session | null) => {
      // avoid using async function as callback of supabase.auth.onAuthStateChange
      // see more [here](https://supabase.com/docs/reference/javascript/auth-onauthstatechange)
      setTimeout(async () => {
        console.log("Auth state change:", event, session?.user?.id);
        setIsAuthenticated(!!session);
        setLoading(false);
        const userId = session?.user?.id;
        setUserId(userId ?? null);
        // set user context for Sentry, this won't fail if Sentry is disabled
        setSentryUser(
          userId ? { id: userId, email: session?.user?.email } : null
        );

        // PostHog identify/reset. Handles SIGNED_IN, INITIAL_SESSION, and
        // SIGNED_OUT (including token-expiry-driven sign-outs). No-op when
        // PostHog isn't configured (dev/preview).
        if (userId) {
          posthogIdentify(userId, { email: session?.user?.email });
        } else if (event === "SIGNED_OUT") {
          posthogReset();
        }
      }, 0);
    },
    []
  );

  useEffect(() => {
    // Initialize auth state
    supabase.auth
      .getSession()
      .then(({ data }) => handleAuthStateChange("INITIAL_SESSION", data.session));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({ isAuthenticated, loading, userId }),
    [isAuthenticated, loading, userId]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};